import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, ArrowRight, LifeBuoy } from 'lucide-react';
import FAQAccordion from './FAQAccordion';
import { FAQ_CATEGORIES } from '../data/faqPage';

// Flatten every FAQ item once, tagged with its category for search + display.
const ALL_ITEMS = FAQ_CATEGORIES.flatMap((c) =>
  c.items.map((it) => ({ ...it, cat: c.label, catId: c.id }))
);

// Instant, self-contained FAQ search used on the home + support pages.
// Backed by the same content as the dedicated /faq page.
export default function FaqSearch({ placeholder = 'Search questions…', maxResults = 6 }) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  const results = useMemo(() => {
    const base = q
      ? ALL_ITEMS.filter((it) => (it.q + ' ' + it.a + ' ' + it.cat).toLowerCase().includes(q))
      : ALL_ITEMS.slice(0, maxResults);
    return base.slice(0, maxResults);
  }, [q, maxResults]);

  return (
    <div className="mx-auto max-w-2xl" data-testid="faq-search-widget">
      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          data-testid="faq-search-input"
          aria-label="Search FAQs"
          className="h-12 w-full rounded-full border border-slate-200 bg-white pl-12 pr-11 text-sm text-slate-900 outline-none transition-colors focus:border-brand dark:border-white/10 dark:bg-white/[0.03] dark:text-white"
        />
        {query && (
          <button
            onClick={() => setQuery('')}
            data-testid="faq-search-widget-clear"
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {q && (
        <p className="mt-3 text-center text-sm text-slate-500 dark:text-slate-400" data-testid="faq-search-widget-count">
          {results.length} result{results.length === 1 ? '' : 's'} for “{query.trim()}”
        </p>
      )}

      <div className="mt-6">
        {results.length ? (
          <FAQAccordion key={q} items={results} className="mx-0 max-w-none" />
        ) : (
          <div
            className="rounded-2xl border border-dashed border-slate-200 p-8 text-center dark:border-white/10"
            data-testid="faq-search-widget-empty"
          >
            <p className="text-sm font-semibold text-slate-800 dark:text-white">No matching questions</p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Try a different search or reach out to our team.</p>
            <Link
              to="/support"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              <LifeBuoy className="h-4 w-4" /> Contact support
            </Link>
          </div>
        )}
      </div>

      <div className="mt-6 text-center">
        <Link
          to="/faq"
          data-testid="faq-view-all"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:opacity-80"
        >
          Browse all FAQs <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
